'use client'

import { Button, Textarea } from '@mentora/ui-components'
import { useTranslations } from 'next-intl'
import { useState, useTransition } from 'react'
import { StarRating } from './star-rating'

export type SubmitReview = (input: {
  orgSlug: string
  courseSlug: string
  courseId: string
  rating: number
  body?: string
}) => Promise<{ ok: boolean, errorKey?: string }>

interface ReviewFormProps {
  courseId: string
  orgSlug: string
  courseSlug: string
  initial: { rating: number, body: string | null } | null
  submitAction: SubmitReview
}

export function ReviewForm({
  courseId,
  orgSlug,
  courseSlug,
  initial,
  submitAction,
}: ReviewFormProps) {
  const t = useTranslations('reviews')
  const [rating, setRating] = useState(initial?.rating ?? 0)
  const [body, setBody] = useState(initial?.body ?? '')
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [isPending, startTransition] = useTransition()

  function onSubmit() {
    setError(null)
    setSaved(false)
    if (rating < 1) {
      setError(t('errorNoRating'))
      return
    }
    startTransition(async () => {
      const result = await submitAction({
        orgSlug,
        courseSlug,
        courseId,
        rating,
        body: body.trim() || undefined,
      })
      if (result.ok) {
        setSaved(true)
      }
      else {
        setError(t('submitError'))
      }
    })
  }

  return (
    <div className="rounded-xl border border-border bg-card p-4 sm:p-5">
      <p className="text-sm font-semibold text-foreground">
        {initial ? t('editTitle') : t('writeTitle')}
      </p>
      <div className="mt-3">
        <StarRating
          value={rating}
          onChange={setRating}
          size={26}
          label={n => t('starsLabel', { count: n })}
        />
      </div>
      <Textarea
        value={body}
        onChange={e => setBody(e.target.value)}
        maxLength={5000}
        rows={4}
        placeholder={t('bodyPlaceholder')}
        className="mt-3"
      />
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Button type="button" onClick={onSubmit} disabled={isPending}>
          {isPending ? t('saving') : initial ? t('update') : t('submit')}
        </Button>
        {saved && (
          <span className="text-sm text-[var(--color-success)]">
            {t('saved')}
          </span>
        )}
        {error && (
          <span className="text-sm text-destructive">{error}</span>
        )}
      </div>
    </div>
  )
}
